import { useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { useFetch } from "@/hooks/useFetch";
import { usePermissions } from "@/lib/permissions";
import { driverService, vehicleService, tripService, alertService } from "@/services/api";
import { PageHeader } from "@/components/common/PageHeader";
import { DriverStatusBadge } from "./Drivers";
import { StatItem } from "@/components/common/Telemetry";
import { SkeletonList } from "@/components/common/Skeletons";
import { EmptyState } from "@/components/common/EmptyState";
import { BarTrend } from "@/components/common/Charts";
import { AddDriverDialog } from "@/components/dialogs/AddDriverDialog";
import { AssignVehicleDialog } from "@/components/dialogs/AssignVehicleDialog";
import { relativeTime, dateTime, clockTime, fmtKm } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Avatar, AvatarFallback } from "@/components/ui/avatar";
import { ArrowLeft, Phone, Mail, Car, Route as RouteIcon, ShieldAlert, Pencil, Link2, Link2Off } from "lucide-react";
import { toast } from "sonner";

export default function DriverDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { isManager } = usePermissions();
  const { data: d, loading, refetch } = useFetch(() => driverService.get(id), [id]);
  const [editOpen, setEditOpen] = useState(false);
  const [assignOpen, setAssignOpen] = useState(false);
  const [unassigning, setUnassigning] = useState(false);

  if (loading) return <div className="p-6"><SkeletonList rows={5} /></div>;
  if (!d) return <div className="p-8"><EmptyState title="Driver not found" action="Back to Drivers" onAction={() => navigate("/drivers")} /></div>;

  const veh = d.assignedVehicleId ? vehicleService.byId(d.assignedVehicleId) : null;
  const trips = veh ? tripService.forVehicle(veh.id) : [];
  const safetyEvents = veh ? alertService.forVehicle(veh.id).filter((a) => a.category === "driving") : [];
  const weekly = [{ day: "Mon", trips: 3 }, { day: "Tue", trips: 4 }, { day: "Wed", trips: 2 }, { day: "Thu", trips: 5 }, { day: "Fri", trips: 4 }, { day: "Sat", trips: 1 }, { day: "Sun", trips: d.tripsToday }];
  const breakdown = d.safetyBreakdown;

  const handleUnassign = async () => {
    setUnassigning(true);
    try {
      await driverService.unassign(d.id);
      toast.success("Driver unassigned");
      refetch();
    } catch (err) {
      toast.error(err.response?.data?.error || "Could not unassign driver");
    } finally {
      setUnassigning(false);
    }
  };

  return (
    <div className="p-4 md:p-6 lg:p-8">
      <button onClick={() => navigate("/drivers")} className="mb-4 inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground"><ArrowLeft className="h-4 w-4" /> Drivers</button>

      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="flex items-center gap-4">
          <Avatar className="h-16 w-16 border border-border/60"><AvatarFallback className="bg-secondary text-lg font-semibold">{d.initials}</AvatarFallback></Avatar>
          <div>
            <h1 className="font-display text-3xl font-semibold tracking-tight">{d.name}</h1>
            <div className="mt-1.5"><DriverStatusBadge status={d.status} /></div>
          </div>
        </div>
        <div className="flex flex-wrap gap-2">
          {veh && <Button variant="secondary" onClick={() => navigate(`/vehicles/${veh.id}`)}><Car className="mr-1.5 h-4 w-4" /> {veh.name}</Button>}
          {isManager && (veh ? (
            <Button variant="destructive" disabled={unassigning} onClick={handleUnassign} data-testid="unassign-driver"><Link2Off className="mr-1.5 h-4 w-4" /> Unassign</Button>
          ) : (
            <Button variant="secondary" onClick={() => setAssignOpen(true)} data-testid="assign-driver"><Link2 className="mr-1.5 h-4 w-4" /> Assign Vehicle</Button>
          ))}
          {isManager && (
            <Button variant="secondary" onClick={() => setEditOpen(true)} data-testid="edit-driver"><Pencil className="mr-1.5 h-4 w-4" /> Edit</Button>
          )}
        </div>
      </div>

      <div className="mt-6 grid gap-4 lg:grid-cols-[320px_1fr]">
        <Card className="border-border/70 bg-card/60 p-5">
          <h3 className="mb-4 font-display text-base font-medium">Profile</h3>
          <div className="space-y-3 text-sm">
            <Row icon={Phone} label="Phone" value={d.phone || "—"} />
            <Row icon={Mail} label="Email" value={d.email || "—"} />
            <Row icon={Car} label="Assigned vehicle" value={veh?.name || "Unassigned"} />
          </div>
        </Card>

        <div className="space-y-4">
          <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
            <StatItem label="Trips today" value={d.tripsToday} />
            <StatItem label="Distance today" value={fmtKm(d.distanceToday)} />
            <StatItem label="Safety events" value={d.safetyEvents} />
            <StatItem label="Safety score" value={d.safetyScore ?? "—"} sub="out of 100" />
          </div>
          <Card className="border-border/70 bg-card/60 p-5">
            <h3 className="mb-3 font-display text-base font-medium">Trips this week</h3>
            <BarTrend data={weekly} x="day" y="trips" height={200} />
          </Card>
        </div>
      </div>

      {breakdown && (
        <Card className="mt-4 border-border/70 bg-card/60 p-5">
          <div className="mb-3 flex items-center justify-between">
            <h3 className="font-display text-base font-medium">Safety score breakdown</h3>
            <span className="text-xs text-muted-foreground">Last {breakdown.period_days} days</span>
          </div>
          {breakdown.insufficient_data ? (
            <p className="text-sm text-muted-foreground">
              Not enough completed-trip distance yet ({breakdown.total_distance_km.toFixed(1)} mi) to compute a reliable score.
            </p>
          ) : (
            <div className="grid gap-3 sm:grid-cols-3">
              <StatItem label="Hard braking" value={`${breakdown.hard_braking_events}`} sub={`${breakdown.hard_braking_per_100km}/100mi · −${breakdown.penalty_hard_braking} pts`} />
              <StatItem label="Hard acceleration" value={`${breakdown.hard_accel_events}`} sub={`${breakdown.hard_accel_per_100km}/100mi · −${breakdown.penalty_hard_accel} pts`} />
              <StatItem label="Speeding trips" value={`${breakdown.speeding_trips}`} sub={`−${breakdown.penalty_speeding} pts`} />
            </div>
          )}
        </Card>
      )}

      <div className="mt-4 grid gap-4 lg:grid-cols-2">
        <Card className="border-border/70 bg-card/60 p-5">
          <h3 className="mb-3 font-display text-base font-medium">Trip history</h3>
          {trips.length === 0 ? <p className="text-sm text-muted-foreground">No trips recorded.</p> : (
            <div className="space-y-2">{trips.slice(0, 5).map((t) => (
              <button key={t.id} onClick={() => navigate(`/trips/${t.id}`)} className="flex w-full items-center gap-3 rounded-xl border border-border/50 bg-secondary/20 p-3 text-left transition hover:border-border hover:bg-secondary/40">
                <RouteIcon className="h-4 w-4 text-muted-foreground" />
                <div className="min-w-0 flex-1"><div className="truncate text-sm font-medium">{t.startLoc} → {t.endLoc}</div><div className="text-xs text-muted-foreground">{clockTime(t.startTime, t.utcOffsetMinutes)} · {fmtKm(t.distanceKm)}</div></div>
              </button>
            ))}</div>
          )}
        </Card>
        <Card className="border-border/70 bg-card/60 p-5">
          <h3 className="mb-3 font-display text-base font-medium">Safety events</h3>
          {safetyEvents.length === 0 ? <p className="text-sm text-muted-foreground">No recent safety events. Great driving.</p> : (
            <div className="space-y-2">{safetyEvents.map((a) => (
              <div key={a.id} className="flex items-start gap-3 rounded-xl border border-amber-400/20 bg-amber-400/5 p-3">
                <ShieldAlert className="mt-0.5 h-4 w-4 text-amber-400" />
                <div className="min-w-0 flex-1"><div className="text-sm font-medium">{a.type}</div><div className="text-xs text-muted-foreground">{dateTime(a.time)} · {relativeTime(a.time)}</div></div>
              </div>
            ))}</div>
          )}
        </Card>
      </div>

      <AddDriverDialog open={editOpen} onOpenChange={setEditOpen} driver={d} onSaved={refetch} />
      <AssignVehicleDialog open={assignOpen} onOpenChange={setAssignOpen} driver={d} onSaved={refetch} />
    </div>
  );
}

function Row({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-2.5"><Icon className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" /><div><div className="text-xs text-muted-foreground">{label}</div><div className="font-medium">{value}</div></div></div>
  );
}
